import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CategoryView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import type { ProductDetailsFormValues } from "@/modules/products/components/productDetailsSchema";
import type { Control, FieldErrors } from "react-hook-form";
import { Controller, useController, useWatch } from "react-hook-form";
import {
  Chip,
  FormField,
  MultiSelect,
  NumberInput,
  TextArea,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatCurrency } from "@/lib/formatting/currency";

type CoreProps = {
  locale: Locale;
  categories: CategoryView[];
  control: Control<ProductDetailsFormValues>;
  errors: FieldErrors<ProductDetailsFormValues>;
};

/**
 * Bilingual name / description / category block of the Edit Product form.
 * Rendered inside the "Product details" SectionCard. Matches the Claude
 * Design 2-col side-by-side AR/EN layout.
 */
export function ProductDetailsCoreFields({
  locale,
  categories,
  control,
  errors,
}: CoreProps) {
  const t = useTranslations();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField
        label={t(keys.products.nameEn)}
        name="nameEn"
        error={errors.nameEn?.message}
      >
        <Controller
          control={control}
          name="nameEn"
          render={({ field }) => (
            <TextInput
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t(keys.placeholders.productNameEn)}
              hasError={Boolean(errors.nameEn)}
            />
          )}
        />
      </FormField>
      <FormField
        label={t(keys.products.nameAr)}
        name="nameAr"
        required
        error={errors.nameAr?.message}
      >
        <Controller
          control={control}
          name="nameAr"
          render={({ field }) => (
            <TextInput
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t(keys.placeholders.productNameAr)}
              required
              hasError={Boolean(errors.nameAr)}
              dir="rtl"
              className="text-end"
            />
          )}
        />
      </FormField>

      <FormField
        label={t(keys.products.shortDescriptionEn)}
        name="shortDescriptionEn"
        error={errors.shortDescriptionEn?.message}
      >
        <Controller
          control={control}
          name="shortDescriptionEn"
          render={({ field }) => (
            <TextArea
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t(keys.placeholders.productDescriptionEn)}
              hasError={Boolean(errors.shortDescriptionEn)}
              rows={3}
            />
          )}
        />
      </FormField>
      <FormField
        label={t(keys.products.shortDescriptionAr)}
        name="shortDescriptionAr"
        error={errors.shortDescriptionAr?.message}
      >
        <Controller
          control={control}
          name="shortDescriptionAr"
          render={({ field }) => (
            <TextArea
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t(keys.placeholders.productDescriptionAr)}
              hasError={Boolean(errors.shortDescriptionAr)}
              dir="rtl"
              rows={3}
            />
          )}
        />
      </FormField>

      <FormField
        label={t(keys.products.categories)}
        name="categoryIds"
        helperText={t(keys.products.categoriesHelper)}
        error={errors.categoryIds?.message}
      >
        <Controller
          control={control}
          name="categoryIds"
          render={({ field }) => (
            <MultiSelect
              options={categories.map((c) => ({
                value: c.id,
                label: pickLocalized(locale, c.nameAr, c.nameEn),
              }))}
              value={field.value ?? []}
              onChange={field.onChange}
              aria-label={t(keys.products.categories)}
              placeholder={t(keys.products.categoriesPlaceholder)}
              searchPlaceholder={t(keys.common.search)}
              noResultsLabel={t(keys.search.noResults)}
            />
          )}
        />
      </FormField>
    </div>
  );
}

type PricingProps = {
  locale: Locale;
  control: Control<ProductDetailsFormValues>;
  errors: FieldErrors<ProductDetailsFormValues>;
};

/**
 * Pricing block. 4-column row (Price / Compare at / Cost per item placeholder
 * / Live preview tile). Availability toggles (isActive, forceOutOfStock) live
 * in the sidebar cards via `useFormContext`, not here.
 */
export function ProductPricingFields({
  locale,
  control,
  errors,
}: PricingProps) {
  const t = useTranslations();
  const { field: discountField } = useController({
    control,
    name: "discountedPrice",
  });
  // Seed the toggle from whether a discount already exists. Turning it off
  // clears the value so "no discount" is an explicit state, not an empty field.
  const initialDiscount =
    typeof discountField.value === "number"
      ? discountField.value
      : Number(discountField.value);
  const [saleOn, setSaleOn] = useState(
    Number.isFinite(initialDiscount) && initialDiscount > 0,
  );
  const toggleSale = (next: boolean) => {
    setSaleOn(next);
    if (!next) discountField.onChange(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label={t(keys.common.price)}
          name="price"
          required
          error={errors.price?.message}
        >
          <Controller
            control={control}
            name="price"
            render={({ field }) => (
              <NumberInput
                name={field.name}
                allowDecimal
                value={
                  typeof field.value === "number"
                    ? String(field.value)
                    : field.value ?? ""
                }
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t(keys.placeholders.price)}
                required
                hasError={Boolean(errors.price)}
              />
            )}
          />
        </FormField>
        <FormField
          label={t(keys.products.variantCostLabel)}
          name="cost"
          error={errors.cost?.message}
        >
          <Controller
            control={control}
            name="cost"
            render={({ field }) => (
              <NumberInput
                name={field.name}
                allowDecimal
                value={
                  typeof field.value === "number"
                    ? String(field.value)
                    : field.value ?? ""
                }
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t(keys.placeholders.price)}
                hasError={Boolean(errors.cost)}
              />
            )}
          />
        </FormField>
        <PricingLivePreview locale={locale} control={control} />
      </div>

      {/* Discount — opt-in toggle reveals the price-after-discount field */}
      <div className="flex flex-col gap-2.5 rounded-lg border border-[color:var(--border-light)] p-3">
        <label className="flex items-center gap-2">
          <ToggleInput
            name="product-sale-toggle"
            checked={saleOn}
            onChange={(e) => toggleSale(e.target.checked)}
            ariaLabel={t(keys.products.variantOnSale)}
          />
          <span className="text-[13px] font-medium text-[color:var(--text-primary)]">
            {t(keys.products.variantOnSale)}
          </span>
        </label>
        {saleOn ? (
          <FormField
            label={t(keys.products.priceAfterDiscount)}
            name="discountedPrice"
            error={errors.discountedPrice?.message}
          >
            <span className="block max-w-[220px]">
              <NumberInput
                name={discountField.name}
                allowDecimal
                value={
                  typeof discountField.value === "number"
                    ? String(discountField.value)
                    : discountField.value ?? ""
                }
                onChange={discountField.onChange}
                onBlur={discountField.onBlur}
                placeholder={t(keys.placeholders.discountedPrice)}
                hasError={Boolean(errors.discountedPrice)}
              />
            </span>
          </FormField>
        ) : null}
      </div>
    </div>
  );
}

type PricingLivePreviewProps = {
  locale: Locale;
  control: Control<ProductDetailsFormValues>;
};

function PricingLivePreview({ locale, control }: PricingLivePreviewProps) {
  const t = useTranslations();
  const price = useWatch({ control, name: "price" });
  const discountedPrice = useWatch({ control, name: "discountedPrice" });

  const priceNum = typeof price === "number" ? price : Number(price) || 0;
  const discountedNum =
    typeof discountedPrice === "number"
      ? discountedPrice
      : discountedPrice != null && discountedPrice !== ""
        ? Number(discountedPrice)
        : null;

  const hasDiscount =
    discountedNum != null && discountedNum > 0 && discountedNum < priceNum;
  const discountPercent = hasDiscount
    ? Math.round((1 - discountedNum / priceNum) * 100)
    : 0;
  const currentPrice = hasDiscount ? discountedNum : priceNum;

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-[color-mix(in_oklab,var(--accent-500)_30%,var(--card))] bg-gradient-to-b from-[color-mix(in_oklab,var(--accent-500)_6%,var(--card))] to-[color-mix(in_oklab,var(--accent-500)_12%,var(--card))] p-3">
      <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
        {t(keys.products.editPricingLivePreview)}
      </span>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-display font-bold text-[color:var(--text-primary)]">
          {formatCurrency(currentPrice, locale)}
        </span>
        {hasDiscount ? (
          <>
            <span className="font-mono text-meta text-[color:var(--text-muted)] line-through">
              {formatCurrency(priceNum, locale)}
            </span>
            <Chip tone="danger" size="sm">
              −{discountPercent}%
            </Chip>
          </>
        ) : null}
      </div>
    </div>
  );
}
